import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import { TIPOS_CARGO } from "../Admin/CargosContenedor/CargoContenedorModal";
import { auth, fmt, redondea } from "./finanzasUtils";

const URL_GASTOS = process.env.REACT_APP_URL_GASTOS_CONTENEDOR;
const URL_CARGOS = process.env.REACT_APP_URL_CARGOS_CONTENEDOR;
const URL_LOGS = process.env.REACT_APP_URL_LOGS;

// Alta de varios gastos o varios cargos de un contenedor en una sola captura.
//  abierto: { modo: "gastos" | "cargos", contenedor }
export default function ModalGenerar({ abierto, cerrar, user, conceptos, brokers, tcDia, onSaved }) {
  const { modo, contenedor } = abierto || {};
  const esCargo = modo === "cargos";
  const nuevoRenglon = () => ({
    id: uuidv4(), concepto: "", descripcion: "", tipo: "", importe: "", iva: "", broker: contenedor ? contenedor.idBrokers : "",
  });

  const [fecha, setFecha] = useState("");
  const [moneda, setMoneda] = useState("MXN");
  const [tipoCambio, setTipoCambio] = useState("");
  const [renglones, setRenglones] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!abierto) return;
    setFecha(moment().format("YYYY-MM-DD"));
    setMoneda("MXN");
    setTipoCambio(tcDia);
    setRenglones([nuevoRenglon()]);
  }, [abierto]); // eslint-disable-line

  const cambia = (id, campo, valor) => setRenglones((rs) => rs.map((r) => (r.id === id ? { ...r, [campo]: valor } : r)));
  const totalDe = (r) => {
    if (!esCargo) return redondea((Number(r.importe) || 0) + (Number(r.iva) || 0));
    return redondea((Number(r.importe) || 0) * (moneda === "USD" ? Number(tipoCambio) || 0 : 1));
  };
  const total = redondea(renglones.reduce((s, r) => s + totalDe(r), 0));

  const payload = (r) => {
    const comun = { fecha, conceptosGastos: r.concepto, descripcion: r.descripcion.trim(), contenedores: contenedor._id, clientes: contenedor.idClientes };
    if (esCargo) {
      return {
        ...comun, tipo: r.tipo, moneda, total: totalDe(r),
        totalUSD: moneda === "USD" ? Number(r.importe) : 0,
        tipoCambio: moneda === "USD" ? Number(tipoCambio) : 1,
      };
    }
    return {
      ...comun, brokers: r.broker, subtotal: Number(r.importe) || 0, iva: Number(r.iva) || 0, total: totalDe(r),
      abonos: 0, saldo: totalDe(r), autorizado: "No",
    };
  };

  const guardar = async () => {
    const incompleto = renglones.some((r) => !r.concepto || !r.descripcion.trim() || !(totalDe(r) > 0) || (esCargo ? !r.tipo : !r.broker));
    if (!fecha || incompleto) {
      Swal.fire({ icon: "warning", title: "Faltan datos", text: `Cada renglón necesita ${esCargo ? "tipo" : "broker"}, concepto, descripción e importe` });
      return;
    }
    setGuardando(true);
    const url = esCargo ? URL_CARGOS : URL_GASTOS;
    const pendientes = [...renglones];
    let guardados = 0;
    try {
      for (const r of renglones) {
        await axios.post(url, payload(r), auth());
        pendientes.shift();
        guardados += 1;
      }
      axios.post(
        URL_LOGS,
        { tipo: esCargo ? "Crear Cargo" : "Crear Gasto", detalle: `Contenedor: ${contenedor.referencia} / Total: ${total}`, user: user.id },
        auth()
      );
      Swal.fire({ icon: "success", title: esCargo ? "Cargos guardados" : "Gastos guardados", timer: 1300, showConfirmButton: false });
      cerrar();
    } catch (err) {
      // Se quitan los que sí entraron para que un reintento no los duplique
      setRenglones(pendientes);
      Swal.fire({
        icon: "error",
        title: "No se pudo guardar",
        text: `Se guardaron ${guardados} renglones. ${err?.response?.data?.message || err.message}`,
      });
    } finally {
      setGuardando(false);
      if (guardados) onSaved();
    }
  };

  const listaConceptos = [...conceptos].sort((a, b) => (a.name > b.name ? 1 : -1));
  const listaBrokers = [...brokers].sort((a, b) => (a.name > b.name ? 1 : -1));

  return (
    <Modal size="xl" isOpen={!!abierto} toggle={cerrar}>
      <ModalHeader toggle={cerrar}>
        <span>{esCargo ? "Cargos al cliente" : "Gastos del broker"} · Contenedor {contenedor ? contenedor.referencia : ""}</span>
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={3} className="mb-2">
            <Label>Fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </Col>
          <Col md={5} className="mb-2">
            <Label>Cliente</Label>
            <Input type="text" value={contenedor ? contenedor.cliente || "" : ""} disabled />
          </Col>
          {esCargo ? (
            <>
              <Col md={2} className="mb-2">
                <Label>Moneda</Label>
                <Input type="select" value={moneda} onChange={(e) => setMoneda(e.target.value)}>
                  <option value="MXN">MXN</option>
                  <option value="USD">USD</option>
                </Input>
              </Col>
              {moneda === "USD" ? (
                <Col md={2} className="mb-2">
                  <Label>Tipo de cambio</Label>
                  <Input type="number" min="0" step="any" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} />
                </Col>
              ) : null}
            </>
          ) : null}
        </Row>

        <Table size="sm" className="ca-renglones">
          <thead>
            <tr>
              <th>{esCargo ? "Tipo" : "Broker"}</th>
              <th>Concepto</th>
              <th>Descripción</th>
              <th className="td-num">{esCargo ? `Importe ${moneda}` : "Subtotal"}</th>
              {!esCargo ? <th className="td-num">IVA</th> : null}
              <th className="td-num">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((r) => (
              <tr key={r.id}>
                <td>
                  {esCargo ? (
                    <Input bsSize="sm" type="select" value={r.tipo} onChange={(e) => cambia(r.id, "tipo", e.target.value)}>
                      <option value="">Selecciona</option>
                      {TIPOS_CARGO.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                    </Input>
                  ) : (
                    <Input bsSize="sm" type="select" value={r.broker} onChange={(e) => cambia(r.id, "broker", e.target.value)}>
                      <option value="">Selecciona</option>
                      {listaBrokers.map((x) => <option key={x._id} value={x._id}>{x.name}</option>)}
                    </Input>
                  )}
                </td>
                <td>
                  <Input bsSize="sm" type="select" value={r.concepto} onChange={(e) => cambia(r.id, "concepto", e.target.value)}>
                    <option value="">Selecciona</option>
                    {listaConceptos.map((x) => <option key={x._id} value={x._id}>{x.name}</option>)}
                  </Input>
                </td>
                <td><Input bsSize="sm" type="text" value={r.descripcion} onChange={(e) => cambia(r.id, "descripcion", e.target.value)} /></td>
                <td><Input bsSize="sm" type="number" min="0" step="any" value={r.importe} onChange={(e) => cambia(r.id, "importe", e.target.value)} /></td>
                {!esCargo ? <td><Input bsSize="sm" type="number" min="0" step="any" value={r.iva} onChange={(e) => cambia(r.id, "iva", e.target.value)} /></td> : null}
                <td className="td-num td-money">{fmt(totalDe(r))}</td>
                <td>
                  <Button size="sm" color="danger" title="Quitar" disabled={renglones.length === 1} onClick={() => setRenglones((rs) => rs.filter((x) => x.id !== r.id))}>
                    <i className="fas fa-minus" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Button size="sm" color="info" onClick={() => setRenglones((rs) => [...rs, nuevoRenglon()])}><i className="fas fa-plus" /> Agregar renglón</Button>
      </ModalBody>
      <ModalFooter>
        <strong style={{ marginRight: "auto" }}>Total {fmt(total)}{esCargo ? " MXN" : ""}</strong>
        <Button color="secondary" onClick={cerrar}><i className="fas fa-times" /> Cerrar</Button>
        <Button color="success" onClick={guardar} disabled={guardando}><i className="fas fa-save" /> Guardar</Button>
      </ModalFooter>
    </Modal>
  );
}
